// Smartify Message Box — the MQTT console, Super Admin only.
//
// A port of the standalone mqttbox-reactjs tool into the app, so diagnosing a
// device doesn't mean opening a separate site and re-entering a broker URL.
//
// Three panes, as in the original:
// At the top: the UNASSIGNED-device reminder — a board nobody can see in any
// app, traced to a house by the network it is on (ui/shared/UnassignedHintBar).
//
//   left    ALL MESSAGES  every device topic, newest first, searchable
//   centre  PUBLISH       a unique number + free text, preset command buttons,
//                         Add/Remove Wi-Fi, and the subscription list
//   right   FILTERED      only the subscribed unique numbers, newest first
//
// The Filtered pane is a FILTER over the same wildcard stream, not a second set
// of broker subscriptions — so adding a device to it costs nothing and shows
// history that already arrived.

import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  Box, Paper, Typography, TextField, Button, IconButton, Divider, Chip,
  Select, MenuItem,
  List, ListItem, ListItemText, Tooltip,
  Checkbox, FormControlLabel,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import WifiIcon from '@mui/icons-material/Wifi';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useNavigate } from 'react-router-dom';
import { copyText } from '../../../core/utils/clipboard';
import { useApp } from '../../../core/state/AppContext';
import {
  startMonitor, stopMonitor, subscribeMessages, logLocal, watchedLines,
  DIR_TO_DEVICE, DIR_FROM_DEVICE, DIR_APP, directionGlyph, displayText,
} from '../../../core/mqtt/mqttMonitor';
import { publishMessage, isOfflineNotice } from '../../../core/mqtt/mqttClient';
import {
  loadSubs, onSubsChange, addSub, removeSub,
} from '../../../core/mqtt/consoleSubs';
import {
  PRESET_COMMANDS, DEBUG_COMMANDS, toDeviceTopic,
} from '../../../core/devices/commands';
// The OFFLINE routes — row writes, not publishes. See markUpdateCode.
import {
  markFirmwareUpdate, PENDING_UPDATE_COMMAND, UPDATE_MARK_COLUMN,
} from '../../../core/devices/firmwareUpdate';
import CallCountsPane from './CallCountsPane';
import DeviceControlCard from '../components/DeviceControlCard';
import DeviceTypePicker from '../../shared/DeviceTypePicker';
import DeviceNameField from '../../shared/DeviceNameField';
import DeviceFirmwarePicker from '../../shared/DeviceFirmwarePicker';
import FirmwareFacts from '../../shared/FirmwareFacts';
import UnassignDeviceButton from '../../shared/UnassignDeviceButton';
import AssignDeviceButton from '../../shared/AssignDeviceButton';
// "I think this is installed at …" for every UNASSIGNED device the log hears.
import UnassignedHintBar from '../../shared/UnassignedHintBar';
import useConsoleDevice from '../../shared/useConsoleDevice';
import { deviceCardWidth, deviceTypeCaption } from '../../../core/devices/deviceMeta';
import { AddWifiDialog, RemoveWifiDialog } from '../../shared/DeviceWifiDialogs';
import RfScanButton from '../../shared/RfScanButton';
import RfLineMenu from '../../shared/RfLineMenu';
import useRfDirectory from '../../../core/hooks/useRfDirectory';
import { parseRfV } from '../../../core/devices/rfCodes';
import { rfMatches, rfUnderLine } from '../../../core/devices/rfIdentify';
import {
  getLocations, getRooms, getDevicesByRoom, getDevicesByUser,
} from '../../../core/api/api';
import {
  primeLabels, getLabel, requestLabel, resolveLabel, subscribeLabels, logLineMatches,
} from '../../../core/devices/deviceLabels';

// The optional panes, in the order they appear. All Messages is not here — it
// is the reason the screen exists and cannot be switched off.
const PANES = [
  { key: 'publish', label: 'Publish' },
  { key: 'filtered', label: 'Filtered' },
  { key: 'calls', label: 'Calls per device' },
];
const DEFAULT_PANES = { publish: true, filtered: true, calls: false };
const PANES_KEY = 'sli_mqtt_panes';

const asArray = (res) => (res && Array.isArray(res.data) ? res.data : []);

// Log density is the point of this screen — you are scanning for a pattern in a
// stream, so more lines on screen beats comfortable text. These match the
// reference console: ~11px monospace on a 1.35 line-height, which fits roughly
// three times as many messages as MUI's defaults did.
const MONO = {
  fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
  fontSize: 10,
  lineHeight: 1.4,
};

// The device editors in the centre pane, at the console's small type: inputs,
// their labels, helper lines and buttons. The console trades size for rows
// everywhere else (MONO is 10px); these were the one block at full size.
const COMPACT_FIELDS = {
  '& .MuiInputBase-input': { fontSize: 12, py: '5px' },
  '& .MuiInputLabel-root': { fontSize: 12 },
  '& .MuiFormHelperText-root': { fontSize: 10, mt: 0.25 },
  '& .MuiButton-root': { fontSize: 11, py: 0.25 },
  '& .MuiTypography-body2': { fontSize: 11 },
  '& .MuiTypography-caption': { fontSize: 10 },
};

// One line per message, exactly as the pane renders it — time, topic, payload,
// and the device name where it is known. Plain text, so it pastes into a
// message, a ticket or a spreadsheet without carrying markup along.
function messagesAsText(messages, labelFor, rfFor) {
  return messages.map((m) => {
    const label = labelFor ? labelFor(m.uno) : '';
    const rf = rfFor ? rfFor(m.text) : null;
    const line = `${m.time} ${directionGlyph(m.dir)} ${displayText(m)}${label ? ` ${label}` : ''}`;
    // An RfV: the full raw line, then the sensor and its technical facts (owner, 2026-10-14).
    if (rf) return `${line}\n      ↳ ${rfUnderLine(rf)}`;
    return line;
  }).join('\n');
}

/*
 * → sent to the device (`<uno>/device`), ← reported by the device
 * (`<uno>/client`), a dot for the console's own notes. Fixed-width so every
 * payload starts in the same column whichever way it went.
 */
function DirectionMark({ dir }) {
  const mark = dir === DIR_TO_DEVICE ? { ch: '→', color: 'warning.light', tip: 'sent to device' }
    : dir === DIR_FROM_DEVICE ? { ch: '←', color: 'success.light', tip: 'reported by device' }
      : dir === DIR_APP ? { ch: '·', color: 'text.secondary', tip: 'this console' }
        : { ch: ' ', color: 'text.secondary', tip: '' };
  return (
    <Box
      component="span"
      title={mark.tip}
      sx={{
        display: 'inline-block', width: '1.1em', color: mark.color, fontWeight: 700, mr: 0.25,
      }}
    >
      {mark.ch}
    </Box>
  );
}

function MessageList({
  title, messages, search, onSearch, onClear, empty, labelFor, rfFor, onRfMenu,
}) {
  // 'idle' | 'ok' | 'fail' — a copy button that cannot say whether it worked is
  // worse than none, because the user only finds out at the paste.
  const [copied, setCopied] = useState('idle');

  const doCopy = async () => {
    // What is ON SCREEN, which after a search is the filtered subset — copying
    // the whole buffer would quietly ignore the filter the user just typed.
    const ok = await copyText(messagesAsText(messages, labelFor, rfFor));
    setCopied(ok ? 'ok' : 'fail');
    setTimeout(() => setCopied('idle'), 1800);
  };

  return (
    <Paper variant="outlined" sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5 }}>
        <Typography fontWeight={700} sx={{ flex: 1, fontSize: 13 }}>{title}</Typography>
        <Chip size="small" label={messages.length} sx={{ height: 18, fontSize: 11 }} />
        <Tooltip title={messages.length ? `Copy these ${messages.length} lines` : 'Nothing to copy'}>
          {/* span so the tooltip still works while the button is disabled */}
          <span>
            <Button
              size="small"
              onClick={doCopy}
              disabled={messages.length === 0}
              startIcon={<ContentCopyIcon sx={{ fontSize: 13 }} />}
              color={copied === 'fail' ? 'error' : copied === 'ok' ? 'success' : 'primary'}
              sx={{ minWidth: 0, fontSize: 11 }}
            >
              {copied === 'ok' ? 'Copied' : copied === 'fail' ? 'Failed' : 'Copy'}
            </Button>
          </span>
        </Tooltip>
        <Button size="small" onClick={onClear} sx={{ minWidth: 0, fontSize: 11 }}>Clear</Button>
      </Box>
      <Box sx={{ px: 1, pb: 0.5 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search…"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          inputProps={{ style: { fontSize: 12, padding: '4px 8px' } }}
        />
      </Box>
      <Divider />
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 1, py: 0.25 }}>
        {messages.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 1, fontSize: 12 }}>
            {empty}
          </Typography>
        ) : messages.map((m) => {
          // Which device this unique number belongs to. A raw number tells you
          // nothing while you are scanning a stream, so the breadcrumb is
          // appended where it is known.
          const label = labelFor ? labelFor(m.uno) : '';
          const rf = rfFor ? rfFor(m.text) : null;
          return (
            <Box
              key={m.id}
              // Right-click an RfV line: set that sensor up from here (RfLineMenu).
              onContextMenu={rf && onRfMenu ? (e) => {
                e.preventDefault();
                onRfMenu({ rf, receiverUno: m.uno, x: e.clientX + 2, y: e.clientY - 6 });
              } : undefined}
              sx={{
                py: '2px',
                ...(rf && onRfMenu ? { cursor: 'context-menu' } : {}),
                borderBottom: 1,
                borderColor: 'divider',
                ...MONO,
                wordBreak: 'break-all',
              }}
            >
              <Box component="span" sx={{ color: 'text.secondary', mr: 0.75 }}>{m.time}</Box>
              {/* Direction FIRST. A command and its echo are textually
                  identical on a one-gang switch and differ only by one word
                  mid-line — which got reported as "the same message twice".
                  See directionOf in core/mqtt/mqttMonitor. */}
              <DirectionMark dir={m.dir} />
              {/* The FULL payload is always shown, an RfV too (owner, 2026-10-14);
                  the sensor it names goes on a second line underneath
                  (core/devices/rfIdentify rfUnderLine: only what the raw line lacks). */}
              {displayText(m)}
              {label && (
                // nowrap so the name never breaks mid-word ("4 Touch Swit / ch"):
                // the row sets break-all for the payload, which must not apply here.
                <Box
                  component="span"
                  sx={{ color: 'success.light', ml: 0.75, fontWeight: 700, whiteSpace: 'nowrap' }}
                >
                  {label}
                </Box>
              )}
              {rf && (
                <Box
                  sx={{ pl: 2, fontWeight: 700, wordBreak: 'normal', color: rf.status === 'named' ? 'info.light' : 'warning.light' }}
                >
                  ↳ {rfUnderLine(rf)}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
}

export default function MqttConsoleScreen() {
  const navigate = useNavigate();
  const { userId, isSuperAdmin } = useApp();

  const [messages, setMessages] = useState([]);
  const [watched, setWatched] = useState([]);
  const [allSearch, setAllSearch] = useState('');
  const [filteredSearch, setFilteredSearch] = useState('');
  const [subs, setSubs] = useState([]);

  const [topic, setTopic] = useState('');
  const [text, setText] = useState('');
  const [subInput, setSubInput] = useState('');

  // Loc / Room / Device pickers
  const [locations, setLocations] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [devices, setDevices] = useState([]);
  const [locId, setLocId] = useState('');
  const [roomId, setRoomId] = useState('');
  const [devUno, setDevUno] = useState('');

  const [allDevices, setAllDevices] = useState([]);

  const [wifiOpen, setWifiOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [marking, setMarking] = useState(false);

  /*
   * Which optional panes are on. Remembered per browser, because it is a
   * workbench preference — someone diagnosing a device keeps Publish open all
   * week, someone auditing traffic keeps Calls open instead, and neither wants
   * to re-tick it on every visit.
   */
  const [panes, setPanes] = useState(() => {
    try {
      const raw = localStorage.getItem(PANES_KEY);
      if (raw) return { ...DEFAULT_PANES, ...JSON.parse(raw) };
    } catch (e) { /* fall through to defaults */ }
    return DEFAULT_PANES;
  });
  useEffect(() => {
    try { localStorage.setItem(PANES_KEY, JSON.stringify(panes)); } catch (e) { /* ignore */ }
  }, [panes]);

  // All Messages is always first and always present; the rest add their share.
  const gridColumns = [
    '1fr',
    panes.publish ? '1.05fr' : '',
    panes.filtered ? '1fr' : '',
    panes.calls ? '1.1fr' : '',
  ].filter(Boolean).join(' ');

  // ── monitor lifecycle ────────────────────────────────────────────────────
  useEffect(() => {
    // Subs first: the monitor files a line into its watched ring by them.
    setSubs(loadSubs());
    startMonitor();
    const off = subscribeMessages((buf, kept) => {
      setMessages([...buf]);
      setWatched([...kept]);
    });
    const offSubs = onSubsChange((s) => setSubs([...s]));
    return () => { off(); offSubs(); stopMonitor(); };
  }, []);

  // Device list, for the breadcrumb and the pickers.
  useEffect(() => {
    if (!userId) return;
    getLocations(userId).then((r) => setLocations(asArray(r))).catch(() => {});
    getDevicesByUser(userId).then((r) => setAllDevices(asArray(r))).catch(() => {});
  }, [userId]);

  useEffect(() => {
    if (!locId) { setRooms([]); setRoomId(''); return; }
    getRooms(userId, locId).then((r) => setRooms(asArray(r))).catch(() => {});
  }, [userId, locId]);

  useEffect(() => {
    if (!roomId) { setDevices([]); setDevUno(''); return; }
    getDevicesByRoom(userId, roomId, locId).then((r) => setDevices(asArray(r))).catch(() => {});
  }, [userId, roomId, locId]);

  // ── publishing ───────────────────────────────────────────────────────────
  // Raw, with no [uno]…#userId envelope — this is a diagnostic console and its
  // whole point is to send EXACTLY what you typed. The reference tool does the
  // same; the firmware accepts both forms.
  const send = useCallback((message) => {
    const t = topic.trim();
    if (!t || !message) return;
    const full = toDeviceTopic(t);
    publishMessage(full, message);
    logLocal(`➡️ published "${message}" to ${full}`);
  }, [topic]);

  // The same, with the `[<uno>]` prefix the ESP32 firmware needs in order to
  // treat a command as addressed rather than as a broadcast every device on the
  // topic acts on (OTA_CLAUDE.md §5.1). Used for the Wi-Fi verbs, which are the
  // ones where a stray broadcast would rewrite a whole site's credentials.
  //
  // Still no `#<userId>` — the ESP8266 does not strip it for those two verbs and
  // it ends up inside the password. See core/devices/commands.js.
  //
  // A topic typed with a '/' is left alone: the operator has addressed something
  // by hand and this must not second-guess it.
  const sendAddressed = useCallback((message) => {
    const t = topic.trim();
    if (!t || !message) return;
    send(t.includes('/') ? message : `[${t}]${message}`);
  }, [topic, send]);

  // uno -> "Location/Room/Device".
  //
  // ⚠️ NOT just the signed-in user's devices. This console watches '#', so most
  // of what scrolls past belongs to OTHER users — and an earlier version, which
  // built this map from getDevicesByUser alone, called every one of them
  // "Unknown device" even though GetDeviceDetailbyUniqueNumber names them
  // perfectly well. The numbers you least recognise are the ones you most need
  // named, so unknown ones are now looked up (see core/devices/deviceLabels).
  useEffect(() => { primeLabels(allDevices); }, [allDevices]);

  // Repaint when a background lookup lands. The labels themselves live in the
  // resolver's cache, not in state.
  const [labelTick, setLabelTick] = useState(0);
  useEffect(() => subscribeLabels(() => setLabelTick((n) => n + 1)), []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const labelFor = useCallback((uno) => getLabel(uno) || '', [labelTick]);

  // An RfV line names the SENSOR: RF number, name, type, meaning, room
  // (core/devices/rfIdentify). Estate-wide — this screen is Super Admin only.
  const rf = useRfDirectory({ all: !!isSuperAdmin });
  const { describe: rfDescribe, ready: rfReady, refreshIfStale: rfRefresh } = rf;
  const rfFor = useCallback((text) => {
    const hit = parseRfV(text);
    return hit && rfReady ? rfDescribe(hit.code) : null;
  }, [rfDescribe, rfReady]);
  const [rfMenu, setRfMenu] = useState(null);   // { rf, receiverUno, x, y }

  // Name every number that appears in the log. Cheap: the resolver ignores
  // numbers it already knows or has already queued.
  useEffect(() => {
    messages.forEach((m) => { if (m.uno && m.uno !== 'app') requestLabel(m.uno); });
  }, [messages]);

  // The device the unique number refers to, so you can see what you're about to
  // command before you command it. Resolved directly rather than through the
  // queue — this is the one number the user is actually looking at.
  // The device the number refers to, resolved once the operator pauses typing,
  // and auto-watched. Drives the Controls card below the presets.
  const {
    device: consoleDevice, reload: reloadConsoleDevice, refreshedAt: consoleReadAt,
  } = useConsoleDevice(topic);

  const [breadcrumb, setBreadcrumb] = useState('');
  useEffect(() => {
    const uno = topic.trim();
    if (!uno) { setBreadcrumb(''); return undefined; }
    // null means never looked up; '' is a real answer ("no such device") and
    // must resolve synchronously, or every repaint would flash "…" again.
    const known = getLabel(uno);
    if (known !== null) { setBreadcrumb(known || 'Unknown device'); return undefined; }
    let alive = true;
    setBreadcrumb('…');
    resolveLabel(uno).then((l) => { if (alive) setBreadcrumb(l || 'Unknown device'); });
    return () => { alive = false; };
  }, [topic, labelTick]);


  /*
   * ── Each CLEAR clears ITS OWN PANE, and only that ──────────────────────
   *
   * ⚠️ Both buttons used to call `clearMessages`, which empties the ONE store
   * every pane is a view of — so clearing Filtered wiped All Messages too, and
   * the other way round. The Filtered pane is not a second list; it is the
   * same array with a `uno` test on it.
   *
   * So the store is no longer touched by either button. Each pane keeps the
   * message id it was cleared at and shows only what arrived after — ids are
   * monotonic (mqttMonitor's `seq`), so "after" is one comparison and needs no
   * timestamps. The buffer is capped at MAX_MESSAGES anyway, so nothing is
   * gained by emptying it, and everything below a pane's floor is still there
   * in the other pane, where it was never asked to go.
   */
  const [allFloor, setAllFloor] = useState(0);
  const [filteredFloor, setFilteredFloor] = useState(0);
  // Newest first, so [0] is the highest id present.
  const floorNow = () => (messages[0] ? messages[0].id : 0);
  const clearAll = () => setAllFloor(floorNow());
  const clearFiltered = () => setFilteredFloor(floorNow());

  const shownAll = useMemo(
    () => messages.filter((m) => m.id > allFloor),
    [messages, allFloor],
  );

  const filtered = useMemo(() => {
    const unos = new Set(subs.map((s) => s.uniqueNumber));
    return watchedLines(messages, watched, unos, filteredFloor);
  }, [messages, watched, subs, filteredFloor]);

  // Search also finds an RF sensor by its RF number, name or hw id.
  const matches = (list, q) => {
    const s = q.trim().toLowerCase();
    return s ? list.filter((m) => logLineMatches(m.text, m.uno, s) || rfMatches(rfFor(m.text), s)) : list;
  };

  // A code we cannot place means the list is old — the server creates a sensor
  // row on first hearing. Throttled inside the hook.
  const newestRf = shownAll.find((m) => parseRfV(m.text));
  const newestRfStatus = newestRf ? rfFor(newestRf.text)?.status : '';
  useEffect(() => {
    if (newestRfStatus === 'new') rfRefresh();
  }, [newestRf && newestRf.id, newestRfStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  /*
   * ── mark update ──────────────────────────────────────────────────────────
   * Writes BOTH server-side routes on the device's install row: the parked
   * command it polls for, and the mark it re-reads on every Wi-Fi connect.
   * REST writes, not publishes, so they work on a device that is not connected
   * — which is the case this console is usually open for.
   *
   * ⚠️ NOTHING CONFIRMS EITHER WAS PICKED UP, and the two expire differently:
   * the parked slot is emptied by the server as soon as the device reads it,
   * whether or not the flash then worked; the mark survives until one does. The
   * log line says what was written, not what happened — the same honesty rule
   * the publish side follows.
   */
  const markUpdateCode = async () => {
    const uno = topic.trim();
    if (!uno || marking) return;
    setMarking(true);
    try {
      await markFirmwareUpdate(uno);
      logLocal(`📌 marked ${uno}: PendingCommand = "${PENDING_UPDATE_COMMAND}" (next poll, ~20 min)`
        + ` + ${UPDATE_MARK_COLUMN} = 1 (next Wi-Fi connect, stays set until a flash succeeds)`);
    } catch (e) {
      logLocal(`📌 mark FAILED for ${uno}: ${(e && e.message) || e}`);
    } finally {
      setMarking(false);
      // Re-read the row so the "update mark set" chip shows the write landed.
      reloadConsoleDevice();
    }
  };

  // ── wifi ─────────────────────────────────────────────────────────────────
  // The two dialogs live in shared/DeviceWifiDialogs — the home page's ⋮ menu
  // opens the very same ones. They publish through sendAddressed (logged here).
  const openAddWifi = () => { if (topic.trim()) setWifiOpen(true); };
  const openRemoveWifi = () => { if (topic.trim()) setRemoveOpen(true); };

  if (!isSuperAdmin) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="text.secondary">This page is for Super Admin only.</Typography>
      </Box>
    );
  }

  // Compact command buttons — these are one-tap actions in a dense tool, so they
  // are sized to fit many per row rather than to be prominent individually.
  const btn = {
    textTransform: 'none',
    minWidth: 0,
    fontSize: 11,
    py: 0.25,
    px: 1,
    lineHeight: 1.5,
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* No title bar. The app header above already says where you are, and this
          screen is three scrolling logs — every row spent on chrome is a row of
          messages you cannot see. Back lives inline next to Publish; Escape
          works globally. */}
      {/* ⚠️ ALL MESSAGES HAS NO CHECKBOX. It is the reason the screen exists —
          the wildcard stream everything else filters or explains — so it is the
          one pane that cannot be switched off. Four panes at once leaves each
          too narrow to read; the switches let the operator spend the width on
          whichever the job needs. */}
      {/* The reminder: an unassigned device that just spoke, with where the
          network says it lives and Assign ready to go (siteGuess). Above the
          panes, because it is the one thing here that asks for an action. */}
      <Box sx={{ px: 1, pt: 0.5 }}>
        <UnassignedHintBar messages={messages} onPick={setTopic} />
      </Box>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 0.5, px: 1, pt: 0.5, flexWrap: 'wrap',
      }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Show:</Typography>
        {PANES.map((p) => (
          <FormControlLabel
            key={p.key}
            sx={{ mr: 0.5 }}
            control={(
              <Checkbox
                size="small"
                sx={{ p: 0.25 }}
                checked={!!panes[p.key]}
                onChange={(e) => setPanes((cur) => ({ ...cur, [p.key]: e.target.checked }))}
              />
            )}
            label={<Typography variant="caption">{p.label}</Typography>}
          />
        ))}
      </Box>

      <Box sx={{
        flex: 1, minHeight: 0, display: 'grid', gap: 0.75, p: 0.75,
        // The visible panes share the width; hiding one gives its space to the
        // others rather than leaving a gap.
        gridTemplateColumns: { xs: '1fr', md: gridColumns },
      }}
      >
        {/* ── ALL ─────────────────────────────────────────────────────── */}
        <MessageList
          title="All Messages"
          messages={matches(shownAll, allSearch)}
          search={allSearch}
          onSearch={setAllSearch}
          onClear={clearAll}
          labelFor={labelFor}
          rfFor={rfFor}
          onRfMenu={setRfMenu}
          empty="Listening… messages appear here as devices report."
        />

        {/* ── PUBLISH ─────────────────────────────────────────────────── */}
        {panes.publish && (
        <Paper variant="outlined" sx={{ p: 0.75, overflowY: 'auto', minHeight: 0 }}>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <Tooltip title="Back (Esc)">
              <IconButton size="small" onClick={() => navigate('/home')} sx={{ p: 0.5 }}>
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {/* Wide enough for a full unique number — it was being clipped to
                "93580!", which is exactly the field you need to read. */}
            <TextField
              size="small" placeholder="Unique number" value={topic}
              onChange={(e) => setTopic(e.target.value)}
              sx={{ width: 118, flexShrink: 0 }}
              inputProps={{ style: { ...MONO, fontSize: 12, padding: '5px 6px' } }}
            />
            <TextField
              size="small" placeholder="Msg" value={text} fullWidth
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { send(text); setText(''); } }}
              inputProps={{ style: { fontSize: 12, padding: '5px 6px' } }}
            />
            <Button
              variant="contained" size="small"
              onClick={() => { send(text); setText(''); }}
              disabled={!topic.trim() || !text.trim()}
              sx={{ ...btn, px: 1, minWidth: 36 }}
            >
              <SendIcon fontSize="small" />
            </Button>
            {/* An RfV line in the log is a sensor nobody named yet — scan it
                into one without leaving the console. Listens only. */}
            <RfScanButton iconOnly sx={{ p: 0.5, flexShrink: 0 }} />
          </Box>

          <Typography sx={{ display: 'block', fontSize: 11, color: 'text.secondary', minHeight: 15 }}>
            {breadcrumb}
          </Typography>

          {/* Every command in ONE wrapping row. They were on three rows with
              full-width buttons, which pushed the subscription controls off the
              visible area for no gain — these are one-tap actions, not choices
              that need emphasis. */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
            {PRESET_COMMANDS.map((c) => (
              <Button key={c} size="small" variant="contained" sx={btn}
                disabled={!topic.trim()} onClick={() => send(c)}
              >
                {c}
              </Button>
            ))}
            {DEBUG_COMMANDS.map((c) => (
              <Button key={c} size="small" variant="contained" color="success" sx={btn}
                disabled={!topic.trim()} onClick={() => send(c)}
              >
                {c}
              </Button>
            ))}
            <Button size="small" variant="contained" sx={btn}
              startIcon={<WifiIcon sx={{ fontSize: 14 }} />}
              disabled={!topic.trim()} onClick={openAddWifi}
            >
              add wifi
            </Button>
            <Button size="small" variant="contained" color="error" sx={btn}
              startIcon={<WifiOffIcon sx={{ fontSize: 14 }} />}
              disabled={!topic.trim()} onClick={openRemoveWifi}
            >
              remove wifi
            </Button>

            {/*
              ⚠️ MARK, NOT SEND — and that is the whole difference from the
              `updatecode` preset two buttons to the left.
                 updatecode   publishes over MQTT. Reaches a device that is
                              LISTENING RIGHT NOW, and vanishes if it is not:
                              publishing to a device that is not connected is
                              not an error, it simply goes nowhere.
                 mark update  writes two columns on the device's row. The
                              firmware asks for the parked command on its own
                              ~20 minute poll and re-reads the mark on every
                              Wi-Fi connect, so between them they reach a device
                              that is asleep, unplugged or out of range —
                              whenever it next wakes.
              On a bench that distinction is the one you need most: the device
              you are trying to fix is usually the one not answering.
            */}
            <Tooltip title="Park “update code” AND set the update mark on the device’s row — it runs on its next check-in (~20 min) or its next power-up, even if it is offline now">
              <span>
                <Button
                  size="small" variant="contained" color="warning" sx={btn}
                  startIcon={<SystemUpdateAltIcon sx={{ fontSize: 14 }} />}
                  disabled={!topic.trim() || marking}
                  onClick={markUpdateCode}
                >
                  mark update
                </Button>
              </span>
            </Tooltip>
          </Box>

          {/*
            ── CONTROLS: the device itself, on the bench ─────────────────────
            The presets above are the console's vocabulary — allon, s05,
            getallwifi — but they say nothing about what THIS device is. A
            4-touch wants four buttons and All On/Off; a fan wants its dial; a
            curtain wants open/stop/close. So the real control is drawn here,
            chosen by DeviceID through the same `DeviceControlCard` the room
            grid and /device-admin use. No second implementation to drift.

            Live for free: the card subscribes to `<uno>/client` itself, so the
            buttons repaint on every status the device sends — and that reply
            lands in the Filtered pane, because the number is auto-watched the
            moment it resolves (see ui/shared/useConsoleDevice).

            ⚠️ It publishes for REAL. This is a bench tool on live hardware —
            a key pressed here switches somebody's light — so it is labelled as
            the device's controls, not as a "test".
          */}
          {consoleDevice && (
            <Box sx={{ mt: 0.75 }}>
              {/* The TYPE, not just the name: `DeviceName` is whatever the
                  installer typed ("4 touch switch" on a Scene Controller), and
                  a bench needs to know which DeviceID it is exercising. */}
              <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.6, fontSize: 10 }}>
                Controls · {deviceTypeCaption(consoleDevice)}
              </Typography>
              {/* The bench is where a wrong type is NOTICED — the card below
                  draws whatever DeviceID says — so it is also where it is
                  fixed. Saving re-reads the row, and the card redraws. */}
              {/* Small type for the editors and the facts, so the pane shows
                  more of the device before the control card. Scoped to this
                  block — the card below keeps its normal size. */}
              {/* One editor per line — type (+ ID + Save), name (+ Rename),
                  firmware (+ Set). ⚠️ NOT two per row: the type row alone needs
                  ~310 px (type 160 + ID 78 + Save), so at half the pane it ran
                  into the name field and the labels sat on top of each other
                  (2026-10-08). Every block's own top margin is collapsed into one
                  small uniform gap. */}
              <Box sx={{
                ...COMPACT_FIELDS,
                display: 'flex', flexDirection: 'column', gap: 0.4,
                '& > *': { mt: '0 !important' },
              }}
              >
                <DeviceTypePicker device={consoleDevice} onSaved={reloadConsoleDevice} />
                {/* The same unique number: its name. */}
                <DeviceNameField device={consoleDevice} onSaved={reloadConsoleDevice} />
                {/* Its firmware file — full width, the .ino.bin name is long. */}
                <DeviceFirmwarePicker device={consoleDevice} onSaved={reloadConsoleDevice} />
                {/* Last seen, last updated vs the newest file it would download. */}
                <FirmwareFacts
                device={consoleDevice} refreshedAt={consoleReadAt} onRefresh={reloadConsoleDevice}
                // The newest message FROM this device in the log — it is alive then,
                // whatever the server's last status change says (2026-10-17).
                heardAt={messages.find((m) => m.dir === 'in' && !isOfflineNotice(m.payload) && String(m.uno) === String(consoleDevice?.UniqueNumber))?.at?.getTime?.() ?? null}
              />
                {/* Back to stock, password-guarded — the same action as Device Admin. */}
                {/* A normal-sized button, not a full-width bar (the column would stretch it). */}
                <Box sx={{ alignSelf: 'flex-start', mb: 0.5, display: 'flex', gap: 1 }}>
                  {Number(consoleDevice.LocationID ?? consoleDevice.LocationId) === 0 && (
                    <AssignDeviceButton
                      uniqueNumber={consoleDevice.UniqueNumber} onDone={reloadConsoleDevice}
                      sx={{ px: 1.25, minWidth: 0 }}
                    />
                  )}
                  <UnassignDeviceButton
                    uniqueNumber={consoleDevice.UniqueNumber} onDone={reloadConsoleDevice}
                    device={consoleDevice}
                    sx={{ px: 1.25, minWidth: 0 }}
                  />
                </Box>
              </Box>
              {/* ⚠️ SIZED BY THE PANEL, NOT BY THE COLUMN. Left to fill the
                  column, a single switch became one button 430px wide — a
                  shape no other page draws. `deviceCardWidth` is the rule the
                  desktop strip already uses: 230px for one to four gangs,
                  wider only when the panel has more columns to show. */}
              <Box sx={{ width: deviceCardWidth(consoleDevice.DeviceID), maxWidth: '100%' }}>
                <DeviceControlCard device={consoleDevice} subtitle={deviceTypeCaption(consoleDevice)} bench />
              </Box>
            </Box>
          )}

          <Divider sx={{ my: 0.75 }} />

          {/* Add to the Filtered pane */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <TextField
              size="small" fullWidth placeholder="Unique number to watch"
              value={subInput} onChange={(e) => setSubInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && subInput.trim()) { addSub(subInput.trim()); setSubInput(''); }
              }}
              inputProps={{ style: { fontSize: 12, padding: '5px 6px' } }}
            />
            <Button size="small" variant="outlined" sx={btn} disabled={!subInput.trim()}
              onClick={() => { addSub(subInput.trim()); setSubInput(''); }}
            >
              Sub
            </Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
            <Select size="small" fullWidth displayEmpty value={locId}
              onChange={(e) => setLocId(e.target.value)}
            >
              <MenuItem value=""><em>Loc</em></MenuItem>
              {locations.map((l) => (
                <MenuItem key={l.LocationID} value={l.LocationID}>{l.LocationName}</MenuItem>
              ))}
            </Select>
            <Select size="small" fullWidth displayEmpty value={roomId}
              onChange={(e) => setRoomId(e.target.value)} disabled={!locId}
            >
              <MenuItem value=""><em>Room</em></MenuItem>
              {rooms.map((r) => (
                <MenuItem key={r.RoomID} value={r.RoomID}>{r.RoomName}</MenuItem>
              ))}
            </Select>
            <Select size="small" fullWidth displayEmpty value={devUno}
              onChange={(e) => setDevUno(e.target.value)} disabled={!roomId}
            >
              <MenuItem value=""><em>Device</em></MenuItem>
              {devices.map((d) => (
                <MenuItem key={d.UniqueNumber} value={d.UniqueNumber}>
                  {d.DeviceName || d.UniqueNumber}
                </MenuItem>
              ))}
            </Select>
            <Button size="small" variant="outlined" sx={btn} disabled={!devUno}
              onClick={() => {
                const d = devices.find((x) => String(x.UniqueNumber) === String(devUno));
                addSub(devUno, d?.DeviceName || devUno);
              }}
            >
              Sub
            </Button>
          </Box>

          <Typography variant="subtitle2" sx={{ mt: 0.75, fontSize: 12 }}>
            {`Subscribed (${subs.length})`}
          </Typography>
          <List dense>
            {subs.map((s) => (
              <ListItem
                key={s.uniqueNumber}
                secondaryAction={(
                  <Tooltip title="Stop watching">
                    <IconButton edge="end" size="small" color="error"
                      onClick={() => removeSub(s.uniqueNumber)}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              >
                <ListItemText
                  primary={s.label === s.uniqueNumber ? s.uniqueNumber : `${s.label} · ${s.uniqueNumber}`}
                  primaryTypographyProps={{ sx: MONO }}
                />
              </ListItem>
            ))}
            {subs.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
                Nothing watched yet — add a unique number above.
              </Typography>
            )}
          </List>
        </Paper>
        )}

        {/* ── FILTERED ────────────────────────────────────────────────── */}
        {panes.filtered && (
          <MessageList
            title="Filtered"
            messages={matches(filtered, filteredSearch)}
            search={filteredSearch}
            onSearch={setFilteredSearch}
            onClear={clearFiltered}
            labelFor={labelFor}
            rfFor={rfFor}
            onRfMenu={setRfMenu}
            empty="Add a unique number to watch it here."
          />
        )}

        {/* ── CALLS PER DEVICE ────────────────────────────────────────── */}
        {panes.calls && (
          <Paper variant="outlined" sx={{ p: 0.75, minHeight: 0, display: 'flex' }}>
            {/* Picking a row aims the console at that device — the number you
                have just found chattering is the one you want to talk to. */}
            <CallCountsPane onPickDevice={(uno) => setTopic(String(uno))} />
          </Paper>
        )}
      </Box>

      {/* Add / remove Wi-Fi — shared with the home page (shared/DeviceWifiDialogs). */}
      <AddWifiDialog
        open={wifiOpen} uno={topic.trim()} subtitle={breadcrumb}
        onClose={() => setWifiOpen(false)} sendAddressed={sendAddressed}
      />
      <RemoveWifiDialog
        open={removeOpen} uno={topic.trim()}
        onClose={() => setRemoveOpen(false)} sendAddressed={sendAddressed}
      />
      <RfLineMenu target={rfMenu} onClose={() => setRfMenu(null)} />
    </Box>
  );
}
